import {
  Button as UiKitButton,
  SidePanel as UiKitSidePanel
} from "@modernatx-internal/component-library";
import React from "react";

import { ContentComponentProps, SlideOutPanelButtonContent, SlideOutPanelContent } from "../types";

interface SlideOutPanelContextValue {
  closePanel: (id: string) => void;
  openPanel: (id: string) => void;
  openPanelIds: Record<string, boolean>;
}

const SlideOutPanelContext = React.createContext<SlideOutPanelContextValue>({
  closePanel: () => {},
  openPanel: () => {},
  openPanelIds: {}
});

export const SlideOutPanelProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const [openPanelIds, openPanelIdsSet] = React.useState<Record<string, boolean>>({});

  const openPanel = (id: string) => {
    openPanelIdsSet((current) => ({
      ...current,
      [id]: true
    }));
  };

  const closePanel = (id: string) => {
    openPanelIdsSet((current) => {
      if (!current[id]) {
        return current;
      }

      return {
        ...current,
        [id]: false
      };
    });
  };

  return (
    <SlideOutPanelContext.Provider value={{ closePanel, openPanel, openPanelIds }}>
      {children}
    </SlideOutPanelContext.Provider>
  );
};

type SlideOutPanelProps = Omit<ContentComponentProps<SlideOutPanelContent>, "children"> & {
  children?: React.ReactNode;
};

type SlideOutPanelButtonProps = Omit<
  ContentComponentProps<SlideOutPanelButtonContent>,
  "children"
> & {
  children?: React.ReactNode;
};

export const SlideOutPanel: React.FC<SlideOutPanelProps> = ({ children, id, ...props }) => {
  const { closePanel, openPanelIds } = React.useContext(SlideOutPanelContext);

  return (
    <UiKitSidePanel {...props} visible={!!openPanelIds[id]} onDismiss={() => closePanel(id)}>
      {children}
    </UiKitSidePanel>
  );
};

export const SlideOutPanelButton: React.FC<SlideOutPanelButtonProps> = ({
  children,
  id,
  ...props
}) => {
  const { openPanel } = React.useContext(SlideOutPanelContext);
  const buttonProps = props as React.ComponentProps<typeof UiKitButton>;

  return (
    <UiKitButton {...buttonProps} onClick={() => openPanel(id)}>
      {children}
    </UiKitButton>
  );
};
